import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Card } from 'ui-components/components/card';
import { Text, TextType } from 'ui-components/components/text';
import { SimpleDropdown } from '../../../../../../components/simple-dropdown';
import MultiSelectDropdown from '../../../../../../advanced-search/components/multi-select-dropdown';
import { UserSettingsOptions } from '../../../../../../enums/user-settings/user-settings-options';
import messages from './messages';

import style from './style.module.less';

export const ScanPageSettings = ({
  verifyItemsBy,
  requiredManualConfirmation,
  onVerifyItemsByChange,
  onRequiredManualConfirmationChange,
}) => {
  const scanTypeOptions = [
    {
      id: UserSettingsOptions.upc.settingValue,
      value: UserSettingsOptions.upc.settingValue,
      component: <FormattedMessage {...messages.scanVerifyUPC} />,
    },
    {
      id: UserSettingsOptions.itemSku.settingValue,
      value: UserSettingsOptions.itemSku.settingValue,
      component: <FormattedMessage {...messages.scanVerifyItemSKU} />,
    },
    {
      id: UserSettingsOptions.fulfillmentSku.settingValue,
      value: UserSettingsOptions.fulfillmentSku.settingValue,
      component: <FormattedMessage {...messages.scanVerifyFulfillmentSKU} />,
    },
  ];

  const selectedScanOptions = scanTypeOptions.filter((option) => verifyItemsBy.includes(option.value));

  const confirmationOptions = [
    {
      value: true,
      label: <FormattedMessage {...messages.requireManualConfirmation} />,
    },
    {
      value: false,
      label: <FormattedMessage {...messages.automaticallyConfirm} />,
    },
  ];

  const getMultiSelectLabel = useCallback(() => {
    if (selectedScanOptions.length === 0) {
      return <FormattedMessage {...messages.scanVerifyDropdownPlaceholder} />;
    }

    return selectedScanOptions.map((option, index) => (
      <span key={option.id}>
        {option.component}
        {index < selectedScanOptions.length - 1 && ', '}
      </span>
    ));
  }, [selectedScanOptions]);

  const handleVerifyItemsByChange = useCallback(
    (option) => {
      const currentValues = [...verifyItemsBy];
      const index = currentValues.indexOf(option.value);

      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(option.value);
      }

      onVerifyItemsByChange(currentValues);
    },
    [verifyItemsBy, onVerifyItemsByChange],
  );

  const handleRequiredManualConfirmationChange = useCallback(
    (optionValue) => {
      onRequiredManualConfirmationChange(optionValue);
    },
    [onRequiredManualConfirmationChange],
  );

  return (
    <Card className={style['scanpage-section']}>
      <Text textType={TextType.H4} value={messages.scanPageTitle} />
      <div>
        <div className={style['scan-verify-section']}>
          <Text textType={TextType.Paragraph} value={messages.scanToVerifyTitle} />
          <div className={style['scan-verify-multiselect-dropdown']}>
            <MultiSelectDropdown
              label={getMultiSelectLabel()}
              onOptionClicked={handleVerifyItemsByChange}
              options={scanTypeOptions}
              selectedOptions={selectedScanOptions}
            />
          </div>
        </div>
        <div className={style['scan-verify-section']}>
          <div>
            <Text textType={TextType.Paragraph} value={messages.markAsVerifiedTitle} />
            <Text textType={TextType.Caption} value={messages.markAsVerifiedCaption} />
          </div>
          <div className={style['scan-verify-dropdown-container']}>
            <SimpleDropdown
              className={style['scan-verify-dropdown']}
              items={confirmationOptions.map((option) => ({ key: option.value.toString(), name: option.label }))}
              onItemSelected={handleRequiredManualConfirmationChange}
              selectedItemKey={requiredManualConfirmation.toString()}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};
